import React, { useEffect } from 'react'
import { Card, Table, Tag, Space, Button, Progress, Tooltip, Badge, Typography, Empty, Statistic, Row, Col } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faEye,
  faHourglassHalf,
  faFlask,
  faClock,
  faExclamationTriangle,
  faInbox
} from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'
import { ORDER_STATUS, PRIORITY_LEVEL_VALUES, PRIORITY_LEVELS } from '../utils/constants'
import { useLabOrders } from '../hooks/useLabOrders'

const { Text } = Typography

const PendingResults = ({ onOrderSelect }) => {
  const { orders, fetchOrders, loading } = useLabOrders()

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  // Filter only pending and in-progress orders
  const pendingOrders = orders.filter((order) => order.status === ORDER_STATUS.ORDERED)

  // Calculate time elapsed and estimated completion
  const calculateProgress = (order) => {
    const orderTime = moment(order.createdAt)
    const now = moment()
    const elapsed = now.diff(orderTime, 'hours')
    let progress = 0

    order.timeline.forEach((event) => {
      if (event.status === 'Ordered') {
        progress = 50
      }

      if (event.status === 'Complete') {
        progress = 100
      }

      if (event.status === 'Expired') {
        progress = 0
      }
    })

    return {
      elapsed,
      progress
    }
  }

  const columns = [
    {
      title: 'Order #',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 120,
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Patient',
      key: 'patientName',
      render: (_, record) => (
        <div>
          <div>
            {record.patientRecord.firstName} {record.patientRecord.lastName}
          </div>
          <small style={{ color: '#8c8c8c' }}>{record.patientRecord.idNo}</small>
        </div>
      )
    },
    {
      title: 'Tests',
      dataIndex: ['orderData', 'orderedTests'],
      key: 'orderedTests',
      render: (tests) => (
        <Space wrap>
          {tests.slice(0, 2).map((test, index) => (
            <Tag key={index} icon={<FontAwesomeIcon style={{ marginRight: 4 }} icon={faFlask} />}>
              {test}
            </Tag>
          ))}
          {tests.length > 2 && <Tag>+{tests.length - 2} more</Tag>}
        </Space>
      )
    },
    {
      title: 'Priority',
      dataIndex: ['orderData', 'priority'],
      key: 'priority',
      width: 100,
      render: (priority) => (
        <Tag
          color={
            priority === PRIORITY_LEVEL_VALUES.STAT
              ? 'red'
              : priority === PRIORITY_LEVEL_VALUES.ROUTINE
              ? 'blue'
              : 'green'
          }
          icon={
            priority === PRIORITY_LEVEL_VALUES.STAT ? (
              <FontAwesomeIcon style={{ marginRight: 4 }} icon={faExclamationTriangle} />
            ) : null
          }
        >
          {priority === PRIORITY_LEVEL_VALUES.STAT ? 'Stat' : 'Routine'}
        </Tag>
      ),
      sorter: (a, b) => {
        const priorityOrder = {
          [PRIORITY_LEVELS.STAT]: 0,
          [PRIORITY_LEVELS.ROUTINE]: 1
        }
        return priorityOrder[a.priority] - priorityOrder[b.priority]
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status) => {
        const icon =
          status === ORDER_STATUS.IN_PROGRESS ? (
            <FontAwesomeIcon icon={faFlask} spin />
          ) : (
            <FontAwesomeIcon icon={faHourglassHalf} />
          )

        return (
          <Badge
            status={status === ORDER_STATUS.IN_PROGRESS ? 'processing' : 'warning'}
            text={
              <Space>
                {icon}
                {status}
              </Space>
            }
          />
        )
      }
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 200,
      render: (_, record) => {
        const { elapsed, progress } = calculateProgress(record)

        return (
          <div>
            <Progress percent={Math.round(progress)} size='small' status='active' />
            <Space size='small' style={{ fontSize: 12, color: '#8c8c8c' }}>
              <FontAwesomeIcon icon={faClock} />
              <Text type='secondary'>{elapsed}h elapsed</Text>
            </Space>
          </div>
        )
      }
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type='primary'
          size='small'
          icon={<FontAwesomeIcon icon={faEye} />}
          onClick={() => onOrderSelect(record)}
        >
          View
        </Button>
      )
    }
  ]

  // Calculate statistics
  const urgentCount = pendingOrders.filter((o) => o.orderData.priority === PRIORITY_LEVEL_VALUES.STAT).length
  const inProgressCount = pendingOrders.filter((o) => o.status === ORDER_STATUS.ORDERED).length
  const pendingCount = pendingOrders.filter((o) => o.status === ORDER_STATUS.ORDERED).length

  return (
    <Card style={{ height: '100%' }}>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title='Total Pending'
              value={pendingOrders.length}
              prefix={<FontAwesomeIcon icon={faHourglassHalf} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title='Urgent Orders'
              value={urgentCount}
              prefix={<FontAwesomeIcon icon={faExclamationTriangle} />}
              valueStyle={{ color: urgentCount > 0 ? '#ff4d4f' : '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title='In Progress'
              value={inProgressCount}
              prefix={<FontAwesomeIcon icon={faFlask} />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title='Awaiting Processing'
              value={pendingCount}
              prefix={<FontAwesomeIcon icon={faClock} />}
              valueStyle={{ color: '#8c8c8c' }}
            />
          </Card>
        </Col>
      </Row>

      {pendingOrders.length > 0 || loading ? (
        <Table
          columns={columns}
          dataSource={pendingOrders}
          rowKey='id'
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} pending orders`
          }}
          scroll={{ x: 1200 }}
          rowClassName={(record) => (record.orderData.priority === PRIORITY_LEVEL_VALUES.STAT ? 'urgent-row' : '')}
          loading={loading}
        />
      ) : (
        <Empty
          image={<FontAwesomeIcon icon={faInbox} style={{ fontSize: 64, color: '#8c8c8c' }} />}
          description={
            <Space direction='vertical' align='center'>
              <Text style={{ fontSize: 16, fontWeight: 500 }}>No pending results</Text>
              <Text type='secondary'>There are currently no lab orders awaiting results</Text>
            </Space>
          }
        />
      )}

      <style jsx>{`
        .urgent-row {
          background-color: #fff1f0;
        }
        .urgent-row:hover > td {
          background-color: #ffebe6 !important;
        }
      `}</style>
    </Card>
  )
}

export default PendingResults
